<template>
  <div
    :class="cn('w-full overflow-hidden border border-[#E5E5E5] bg-white shadow-xs rounded-[16px]', containerClass)"
  >
    <div
      class="overflow-auto"
      :style="scrollContainerStyle"
    >
      <table :class="cn('w-full text-left border-collapse', tableClass)">
        <thead
          :class="cn(
            'bg-[#FAFAFA]',
            stickyHeader ? 'sticky top-0 z-10 shadow-[0_1px_0_0_#E5E5E5]' : 'border-b border-[#E5E5E5]',
            headerClass
          )"
        >
          <tr :class="cn('bg-[#FAFAFA]', headerRowClass)">
            <th
              v-if="selectable"
              :class="cn('py-[13px] px-[16px] w-[48px] text-center align-middle bg-[#FAFAFA]', headerCheckboxClass)"
            >
              <TableCheckbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="toggleSelectAll"
              />
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="cn('py-[16px] px-[16px] text-[#404040] font-medium text-[14px] bg-[#FAFAFA]', col.thClass)"
              :style="col.thStyle"
            >
              <slot
                :name="`head(${col.key})`"
                :column="col"
              >{{ col.label }}</slot>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-[#E5E5E5]">
          <!-- Loading State -->
          <tr v-if="loading && (!items || items.length === 0)">
            <td
              :colspan="totalColumnCount"
              class="py-8 text-center"
            >
              <slot name="loading">
                <div class="flex items-center justify-center gap-2">
                  <b-spinner
                    v-if="$options.components['b-spinner'] || $options.components['BSpinner']"
                    variant="primary"
                    label="Loading..."
                    small
                  />
                  <span class="text-sm text-[#737373]">{{ loadingText }}</span>
                </div>
              </slot>
            </td>
          </tr>

          <!-- Empty State -->
          <tr v-else-if="!items || items.length === 0">
            <td
              :colspan="totalColumnCount"
              class="py-8 text-center text-[#737373] text-sm"
            >
              <slot name="empty">
                {{ emptyText }}
              </slot>
            </td>
          </tr>

          <!-- Data Rows -->
          <tr
            v-for="(item, index) in items"
            :key="getItemKey(item, index)"
            :class="cn('hover:bg-[#F9FAFB] transition-colors', rowClass ? rowClass(item, index) : '')"
            @click="$listeners['row-click'] ? onRowClick(item, index, $event) : null"
          >
            <td
              v-if="selectable"
              :class="cn('py-[13px] px-[16px] text-center align-middle', rowCheckboxClass)"
            >
              <TableCheckbox
                :disabled="isSelectableRow && !isSelectableRow(item, index)"
                :checked="localSelected.includes(getItemKey(item, index))"
                @change="() => toggleSelectRow(item, index)"
              />
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              :class="cn('py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]', col.tdClass)"
              :style="col.tdStyle"
            >
              <slot
                :name="`cell(${col.key})`"
                :item="item"
                :value="item[col.key]"
                :index="index"
              >
                {{ formatCellValue(item, col) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import { cn } from '../utils'
import TableCheckbox from './TableCheckbox.vue'

export default {
  name: 'UITable',
  components: { TableCheckbox },
  props: {
    columns: { type: Array, required: true, default: () => [] },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    selected: { type: Array, default: () => [] },
    itemKey: { type: String, default: 'id' },
    isSelectableRow: { type: Function, default: null },
    maxHeight: { type: String, default: '600px' },
    stickyHeader: { type: Boolean, default: true },
    emptyText: { type: String, default: 'Tidak ada data yang ditampilkan' },
    loadingText: { type: String, default: 'Memuat data...' },
    containerClass: { type: String, default: '' },
    tableClass: { type: String, default: '' },
    headerClass: { type: String, default: '' },
    headerRowClass: { type: String, default: '' },
    headerCheckboxClass: { type: String, default: '' },
    rowCheckboxClass: { type: String, default: '' },
    rowClass: { type: Function, default: null },
  },
  data() {
    return {
      localSelected: [...(this.selected || [])],
    }
  },
  computed: {
    scrollContainerStyle() {
      return (!this.maxHeight || this.maxHeight === 'none') ? {} : { maxHeight: this.maxHeight }
    },
    totalColumnCount() {
      return (this.columns ? this.columns.length : 0) + (this.selectable ? 1 : 0)
    },
    isAllSelected() {
      const len = this.items ? this.items.length : 0
      return len > 0 && this.localSelected.length >= len
    },
    isIndeterminate() {
      const len = this.items ? this.items.length : 0
      const count = this.localSelected.length
      return count > 0 && count < len
    },
  },
  watch: {
    selected: {
      deep: true,
      immediate: true,
      handler(val) {
        this.localSelected = [...(val || [])]
      },
    },
  },
  methods: {
    cn,
    getItemKey(item, index) {
      if (item && item[this.itemKey] !== undefined) return item[this.itemKey]
      if (item && item.id !== undefined) return item.id
      return index
    },
    toggleSelectAll(checked) {
      const keys = checked && this.items ? this.items.map((item, idx) => this.getItemKey(item, idx)) : []
      this.localSelected = keys
      this.$emit('update:selected', keys)
      this.$emit('selection-change', keys)
    },
    toggleSelectRow(item, index) {
      const key = this.getItemKey(item, index)
      const idx = this.localSelected.indexOf(key)
      if (idx > -1) {
        this.localSelected.splice(idx, 1)
      } else {
        this.localSelected.push(key)
      }
      this.$emit('update:selected', this.localSelected)
      this.$emit('selection-change', this.localSelected)
    },
    formatCellValue(item, col) {
      const val = item[col.key]
      if (typeof col.formatter === 'function') {
        return col.formatter(val, item)
      }
      return val !== undefined && val !== null ? val : '-'
    },
    onRowClick(item, index, event) {
      this.$emit('row-click', { item, index, event })
    },
  },
}
</script>
